import { type ComponentType, type ReactNode } from 'react';
import { LuSparkle } from 'react-icons/lu';
import { RiEditLine, RiStethoscopeLine } from 'react-icons/ri';
import type { AgentType } from '@/model/Agent.model';

type IconComponent = ComponentType<{ size: number; className: string }>;

const agentIconMap: Record<AgentType, IconComponent> = {
  study_health_check: RiStethoscopeLine,
  study_protocol_parser: RiEditLine,
};

/**
 * Maps each agent type to its kebab-case URL segment. The API exposes agent
 * slugs as snake_case enum values, but web routes use kebab-case, so this map
 * is the single source of truth for translating between the two.
 */
export const agentUrlSlugMap: Record<AgentType, string> = {
  study_health_check: 'study-health-check',
  study_protocol_parser: 'study-protocol-parser',
};

const urlSlugToAgentType = new Map<string, AgentType>(
  (Object.entries(agentUrlSlugMap) as Array<[AgentType, string]>).map(([type, urlSlug]) => [urlSlug, type])
);

/** Resolves a kebab-case URL segment back to its agent type, or undefined if unknown. */
export const parseAgentUrlSlug = (urlSlug: string): AgentType | undefined => urlSlugToAgentType.get(urlSlug);

/** Per-agent display strings for the session view: the friendly `name` and the in-progress `title`. */
interface AgentDisplay {
  /** Human-friendly agent name (shown in the session breadcrumb). */
  name: string;
  /** What the agent is doing, shown as the root node's title. */
  title: string;
}

/**
 * Display strings per agent type, used where only the agent slug is available (e.g. the session page,
 * which gets `agent_type` from the URL but no display copy). The `title` is per-agent because each
 * agent does something different.
 */
export const agentDisplayMap: Record<AgentType, AgentDisplay> = {
  study_health_check: { name: 'Study Health Check', title: 'Generating Report' },
  study_protocol_parser: { name: 'Study Protocol Parser', title: 'Parsing Protocol' },
};

/**
 * Returns the icon element associated with an agent slug.
 * Falls back to a sparkle icon for unrecognized slugs.
 */
export const getAgentIcon = (slug: AgentType): ReactNode => {
  const Icon = agentIconMap[slug] ?? LuSparkle;
  return <Icon size={26} className="blue" />;
};
